import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: (theme) =>
            `linear-gradient(160deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          borderBottom: 1,
          borderColor: 'divider',
          py: 8,
          px: 3,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 3 }}>
            Legal
          </Typography>
          <Typography variant="h2" component="h1" sx={{ my: 2, fontWeight: 700 }}>
            {t('privacyPolicy.title')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
            {t('privacyPolicy.lastUpdated')}. Your privacy is our top priority at IndeeClips.
          </Typography>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          At IndeeClips, your privacy is our top priority. This Privacy Policy explains how we collect, use, disclose
          and safeguard your information when you use our mobile application and website located at indeeclips.com.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          1. Information We Collect
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          We may collect the following types of information:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>
            <strong>Personal Information:</strong> Name, email address, mobile number and profile picture when you
            register or log in.
          </li>
          <li>
            <strong>Usage Data:</strong> Videos watched, search history, watch time and interaction with content to
            improve your experience.
          </li>
          <li>
            <strong>Device Information:</strong> Device model, OS version, IP address, unique device identifiers and app
            version for analytics and security.
          </li>
          <li>
            <strong>Location Data:</strong> General location based on IP address for content recommendations and ad
            targeting.
          </li>
          <li>
            <strong>Log Data:</strong> IP address, browser type, pages visited and time spent on our platform.
          </li>
        </Box>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          2. How We Use Your Information
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          We use your information to:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Provide, operate and maintain our video streaming service</li>
          <li>Personalise your content feed and recommend videos you will love</li>
          <li>Improve app performance and overall user experience</li>
          <li>Communicate updates, offers and support messages</li>
          <li>Monitor and analyse usage patterns to improve our platform</li>
          <li>Display relevant advertisements through our advertising partners</li>
          <li>Ensure platform security and prevent misuse or fraud</li>
          <li>Comply with legal obligations and enforce our Terms of Service</li>
        </Box>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          3. Advertising
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          IndeeClips is a free platform supported by advertising revenue. We work with third-party advertising partners
          including Google AdMob and other ad networks to display ads within our app and website. These partners may use
          cookies and similar tracking technologies to serve personalised ads based on your interests and browsing
          behaviour.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          You can opt out of personalised advertising at any time through your device settings or by visiting the
          Network Advertising Initiative opt-out page.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          4. Data Sharing
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          We do not sell your personal data to third parties. We may share information with:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>
            <strong>Service Providers:</strong> Trusted third parties who help us operate our platform including
            hosting, analytics and payment processing.
          </li>
          <li>
            <strong>Advertising Partners:</strong> To serve relevant advertisements within our app and website.
          </li>
          <li>
            <strong>Legal Authorities:</strong> When required by law, court order or to protect our rights and the
            safety of our users.
          </li>
          <li>
            <strong>Business Transfers:</strong> In case of a merger, acquisition, or sale of all or part of our assets.
          </li>
        </Box>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          5. Data Security
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          We implement appropriate technical and organisational security measures to protect your personal information
          against unauthorised access, alteration, disclosure or destruction. We use encryption, secure servers and
          regular security audits to keep your data safe.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to
          use commercially acceptable means to protect your data, we cannot guarantee its absolute security.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          6. Children's Privacy
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          IndeeClips is not intended for children under the age of 13. We do not knowingly collect personal information
          from children under 13. If you believe we have inadvertently collected such information, please contact us
          immediately at contact@indeeclips.com and we will promptly delete it.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          7. Your Rights
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          You have the right to:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Access the personal data we hold about you</li>
          <li>Request correction of inaccurate personal data</li>
          <li>Request deletion of your personal data</li>
          <li>Opt out of marketing communications at any time</li>
          <li>Withdraw consent for data processing where applicable</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          To exercise any of these rights, please contact us at{' '}
          <Typography
            component="a"
            href="mailto:contact@indeeclips.com"
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            contact@indeeclips.com
          </Typography>
          .
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          8. Cookies
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          Our website uses cookies and similar tracking technologies to enhance your browsing experience, analyse traffic
          and serve personalised content and ads. You can control cookie settings through your browser preferences.
          Please note that disabling cookies may affect some functionality of our website.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          9. Third Party Links
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          Our platform may contain links to third party websites. We are not responsible for the privacy practices or
          content of those websites. We encourage you to read the privacy policies of any third party sites you visit.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          10. Changes to This Policy
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the
          updated policy on this page with a revised date at the top. Your continued use of IndeeClips after any changes
          constitutes your acceptance of the updated policy.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          11. Contact Us
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1, lineHeight: 1.85 }}>
          If you have any questions, concerns or requests regarding this Privacy Policy, please contact us at:
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.85 }}>
          Email:{' '}
          <Typography
            component="a"
            href="mailto:contact@indeeclips.com"
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            contact@indeeclips.com
          </Typography>
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
          Website:{' '}
          <Typography
            component="a"
            href="https://indeeclips.com"
            target="_blank"
            rel="noreferrer"
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            https://indeeclips.com
          </Typography>
        </Typography>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
